import { useEffect, useMemo, useRef, useState } from 'react'
import { reilmAPI } from '../../services/apiClient'
import { isHandledElsewhere } from './requestErrors'
import { buildSavePayload, diffSavePayload, mergeSavePayload, saveLeaves, withLeaves } from './scenarioSave'

export const AUTOSAVE_DEBOUNCE_MS = 800
export const AUTOSAVE_RETRY_BASE_MS = 1000
export const AUTOSAVE_RETRY_MAX_MS = 30000

// A refused body or a scenario that no longer exists fails the same way on every retry.
const NOT_RETRIED = new Set([400, 404])
// Stands for the jobs a run left unsent because pagehide fired: handled like an aborted request.
const STOPPED = { code: 'ERR_CANCELED' }

const statusOf = (err) => err?.response?.status
const isCanceled = (err) => err?.code === 'ERR_CANCELED'
// The request may have been written before it failed (no answer, 5xx, aborted).
const isUncertain = (err) => isCanceled(err) || !err?.response || statusOf(err) >= 500

// Field edits of every scenario the server knows, saved as changed leaves only (D13-D18).
// Behaviour, flush points and failure handling: README.md ("Autosave").
export default function useScenarioAutosave(propertyId, scenarios, { onSaved, delay = AUTOSAVE_DEBOUNCE_MS } = {}) {
  const [error, setErrorState] = useState(null)
  // Scenarios a 404 dropped: the page keeps saying so while one of them is on screen.
  const [goneIds, setGoneIds] = useState(() => new Set())
  const errorRef = useRef(null)
  const scenariosRef = useRef(scenarios)
  scenariosRef.current = scenarios
  const onSavedRef = useRef(onSaved)
  onSavedRef.current = onSaved

  const savedRef = useRef(new Map())
  // id -> Map(leaf -> seq of the request that left it uncertain): always resent until a later success.
  const unknownRef = useRef(new Map())
  const failStatusRef = useRef(new Map())
  const suspendedRef = useRef(new Set())
  const timerRef = useRef(null)
  const queueRef = useRef(Promise.resolve())
  const attemptRef = useRef(0)
  const lastSigRef = useRef('')
  // id -> { body, settled } of the keepalive PATCH still out for that scenario.
  const keepaliveRef = useRef(new Map())
  const seqRef = useRef(0)
  // Bumped by pagehide: a run started or queued before it sends nothing more.
  const epochRef = useRef(0)
  const appliedSeqRef = useRef(new Map())
  const controllersRef = useRef(new Map())
  const aliveRef = useRef(true)
  const payloadCache = useRef(new WeakMap())

  const api = useMemo(() => {
    // Every successful save would otherwise re-render the page to clear an error that is not there.
    const setError = (next) => {
      if (errorRef.current === next) return
      errorRef.current = next
      setErrorState(next)
    }

    const payloadOf = (scenario) => {
      let payload = payloadCache.current.get(scenario)
      if (!payload) {
        payload = buildSavePayload(scenario)
        payloadCache.current.set(scenario, payload)
      }
      return payload
    }

    const pendingDiff = (scenario) => {
      const saved = savedRef.current.get(scenario._id)
      if (!saved || suspendedRef.current.has(scenario._id)) return null
      const next = payloadOf(scenario)
      const diff = diffSavePayload(saved, next)
      const unknown = unknownRef.current.get(scenario._id)
      return unknown ? withLeaves(diff, next, unknown.keys()) : diff
    }

    const pendingJobs = () =>
      scenariosRef.current.map((s) => [s._id, pendingDiff(s)]).filter(([, diff]) => diff !== null)

    const signature = () => {
      const jobs = pendingJobs()
      return jobs.length > 0 ? JSON.stringify(jobs) : ''
    }

    const schedule = (ms) => {
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        flush()
      }, ms)
    }

    // Nothing is left unsaved (a failed value typed back, the failing scenario deleted): no line, and
    // the next failure starts its backoff afresh.
    const idle = () => {
      attemptRef.current = 0
      failStatusRef.current.clear()
      setError(null)
    }

    const markUnknown = (id, diff, seq) => {
      const unknown = unknownRef.current.get(id) ?? new Map()
      for (const leaf of saveLeaves(diff)) unknown.set(leaf, Math.max(unknown.get(leaf) ?? 0, seq))
      unknownRef.current.set(id, unknown)
    }

    const confirmKnown = (id, diff, seq) => {
      const unknown = unknownRef.current.get(id)
      if (!unknown) return
      for (const leaf of saveLeaves(diff)) if (unknown.get(leaf) < seq) unknown.delete(leaf)
      if (unknown.size === 0) unknownRef.current.delete(id)
    }

    // A response for a scenario deleted (or being deleted) meanwhile is dropped, success or failure.
    const dropped = (id) => !savedRef.current.has(id) || suspendedRef.current.has(id)

    // Per scenario, a success older than one already applied is dropped, so a late answer cannot move
    // the client's copy of what is saved back (README.md, "Order", for what it cannot protect).
    const send = (id, diff, keepalive) => {
      const seq = ++seqRef.current
      const body = JSON.stringify(diff)
      const controller = keepalive ? null : new AbortController()
      if (controller) controllersRef.current.set(controller, { id, diff, seq })
      const out = keepalive ? { body } : null
      const request = reilmAPI
        .updateFlipScenario(propertyId, id, diff, { keepalive, signal: controller?.signal })
        .then(
          ({ data }) => {
            if (dropped(id) || seq < (appliedSeqRef.current.get(id) ?? 0)) return
            appliedSeqRef.current.set(id, seq)
            savedRef.current.set(id, mergeSavePayload(savedRef.current.get(id), diff))
            confirmKnown(id, diff, seq)
            failStatusRef.current.delete(id)
            onSavedRef.current?.(id, data?.data?.scenario)
          },
          (err) => {
            if (dropped(id)) return
            if (isUncertain(err)) markUnknown(id, diff, seq)
            // Only the latest failure counts: a 400 followed by a network error is no longer refused.
            if (statusOf(err)) failStatusRef.current.set(id, statusOf(err))
            else failStatusRef.current.delete(id)
            throw err
          }
        )
        .finally(() => {
          if (controller) controllersRef.current.delete(controller)
          if (out && keepaliveRef.current.get(id) === out) keepaliveRef.current.delete(id)
        })
      if (out) {
        out.settled = request.catch(() => {})
        keepaliveRef.current.set(id, out)
      }
      return request
    }

    // Several failures in one run: a retryable one wins (the retry resends everything still unsaved).
    // A 404 adds no line here: the page shows "gone" while that scenario is on screen.
    const onFailure = (failures) => {
      const shown = failures.filter((err) => !isHandledElsewhere(err) && !isCanceled(err) && statusOf(err) !== 404)
      const retryable = shown.find((err) => !NOT_RETRIED.has(statusOf(err)))
      if (!retryable && shown.length > 0) {
        setError('failed')
        return
      }
      if (!retryable) {
        setError(null)
        // Aborted by pagehide and the page stayed (bfcache): send again quietly.
        if (failures.some(isCanceled) && aliveRef.current && signature()) schedule(delay)
        return
      }
      attemptRef.current += 1
      setError('retrying')
      if (aliveRef.current) {
        schedule(Math.min(AUTOSAVE_RETRY_BASE_MS * 2 ** (attemptRef.current - 1), AUTOSAVE_RETRY_MAX_MS))
      }
    }

    const markGone = (id) => {
      forget(id)
      setGoneIds((prev) => new Set(prev).add(id))
    }

    // Reads the diffs when it runs, not when it was asked for, so a queued flush sends the latest
    // values. Each scenario's failure is its own: one scenario's 404 never holds back the others.
    // Keepalive sends every scenario at once (the page may be gone before a first answer); an
    // ordinary run goes one by one and stops once pagehide has fired.
    const run = (keepalive, epoch) => {
      if (epoch !== epochRef.current) {
        // Queued before pagehide, run after a bfcache restore: pagehide already sent what it held.
        if (aliveRef.current && signature()) schedule(delay)
        return Promise.resolve()
      }
      const pending = pendingJobs()
      if (pending.length === 0) {
        idle()
        return Promise.resolve()
      }
      const jobs = pending.filter(([id, diff]) => keepaliveRef.current.get(id)?.body !== JSON.stringify(diff))
      if (jobs.length === 0) return Promise.resolve()
      const failures = []
      const stop = () => {
        if (!failures.includes(STOPPED)) failures.push(STOPPED)
      }
      // The diff is read again right before sending: a scenario deleted (suspended) or saved while an
      // earlier job of this run was out sends nothing (D17).
      const one = ([id]) => {
        const scenario = scenariosRef.current.find((s) => s._id === id)
        const diff = scenario ? pendingDiff(scenario) : null
        if (!diff) return Promise.resolve()
        return send(id, diff, keepalive).catch((err) => {
          // The scenario no longer exists on the server: nothing more can be saved for it.
          if (statusOf(err) === 404) markGone(id)
          failures.push(err)
        })
      }
      // A keepalive of this scenario still out (pagehide, then a bfcache restore) is let settle first,
      // so the server cannot end on the keepalive's older values.
      const ordinary = (job) => {
        const out = keepaliveRef.current.get(job[0])
        if (!out) return one(job)
        return out.settled.then(() => (epoch !== epochRef.current ? stop() : one(job)))
      }
      const sent = keepalive
        ? Promise.all(jobs.map(one))
        : jobs.reduce(
            (chain, job) => chain.then(() => (epoch === epochRef.current ? ordinary(job) : stop())),
            Promise.resolve()
          )
      return sent.then(() => {
        const sig = signature()
        lastSigRef.current = sig
        if (failures.length > 0) {
          onFailure(failures)
          return
        }
        attemptRef.current = 0
        setError(null)
        // A value put back while its PATCH was out differs from the new server copy, and no edit
        // follows to restart the timer.
        if (sig && aliveRef.current) schedule(delay)
      })
    }

    // Queued behind any save in flight, and an ordinary run waits out that scenario's keepalive, so
    // two PATCHes of one scenario never race (README.md, "Order"). Never rejects.
    function flush({ keepalive = false } = {}) {
      clearTimeout(timerRef.current)
      timerRef.current = null
      const epoch = epochRef.current
      const next = queueRef.current.then(() => run(keepalive, epoch))
      queueRef.current = next
      return next
    }

    // pagehide: the page may be gone before the queue drains, so this goes at once as keepalive with
    // the current values of every scenario. An ordinary request still out is aborted (it would die
    // with the page anyway) and what it carried is resent; a scenario whose same values are already
    // out as keepalive (a hidden tab being closed fires both) is skipped.
    const flushNow = () => {
      clearTimeout(timerRef.current)
      timerRef.current = null
      epochRef.current += 1
      controllersRef.current.forEach(({ id, diff, seq }, controller) => {
        if (!dropped(id)) markUnknown(id, diff, seq)
        controller.abort()
      })
      return run(true, epochRef.current)
    }

    function forget(id) {
      suspendedRef.current.delete(id)
      savedRef.current.delete(id)
      appliedSeqRef.current.delete(id)
      unknownRef.current.delete(id)
      failStatusRef.current.delete(id)
    }

    // After a flush: whether this scenario still has values the server does not hold (a failed save),
    // so a copy made from the server's version would miss them.
    const hasUnsaved = (id) => {
      const scenario = scenariosRef.current.find((s) => s._id === id)
      return scenario ? pendingDiff(scenario) !== null : false
    }

    return {
      schedule,
      signature,
      idle,
      flush,
      flushNow,
      // The server's copy of a scenario (loaded, imported, duplicated): what later edits are diffed against.
      markSaved: (scenario) => {
        forget(scenario._id)
        savedRef.current.set(scenario._id, buildSavePayload(scenario))
      },
      tracks: (id) => savedRef.current.has(id),
      // Delete: nothing is saved for this scenario while its request runs (D17).
      suspend: (id) => suspendedRef.current.add(id),
      resume: (id) => {
        suspendedRef.current.delete(id)
        if (signature()) schedule(delay)
      },
      forget,
      hasUnsaved,
      // The server refused this scenario's last save as invalid: the same body fails every time.
      rejected: (id) => failStatusRef.current.get(id) === 400,
    }
  }, [propertyId, delay])

  // One timer for every scenario: restarted only when the unsaved values themselves change, so a
  // re-render, a rename or a Last Modified update starts nothing.
  useEffect(() => {
    const sig = api.signature()
    if (sig && sig !== lastSigRef.current) api.schedule(delay)
    if (!sig && lastSigRef.current) api.idle()
    lastSigRef.current = sig
  }, [scenarios, api, delay])

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') api.flush({ keepalive: true })
    }
    const onPageHide = () => api.flushNow()
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [api])

  // Leaving the page (a link, the browser's back button): one last save, no retries after it.
  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      api.flush()
    }
  }, [api])

  return { ...api, error, goneIds }
}
